import React, { useState, useEffect, useContext } from 'react';
import { ServerConfigContext } from '../context/ServerConfigContext';
import ReactApexChart from 'react-apexcharts';
import './TradingPages.css';

const AlgorithmBacktesting = () => {
  const { getApiUrl, isConfigLoaded } = useContext(ServerConfigContext);

  // 폼 상태
  const [stockCode, setStockCode] = useState('005930'); // 기본값: 삼성전자
  const [stockName, setStockName] = useState('삼성전자');
  const [startDate, setStartDate] = useState('2020-01-01'); // 기본값: 2020-01-01
  const [endDate, setEndDate] = useState(''); // 기본값: 오늘

  // 매수 조건 상태
  const [buyConditions, setBuyConditions] = useState({
    maType: '20', // 이동평균선 종류 (기본값: 20일)
    maDeviation: '-10', // 이동평균선 이격률 (기본값: -10%)
    rsi: '30', // RSI 기준 (기본값: 30 이하)
    maxBuyCount: '3' // 최대 분할 매수 횟수 (기본값: 3)
  });

  // 매도 조건 상태
  const [sellConditions, setSellConditions] = useState({
    requireMaxBuyCount: false, // 최대 분할 매수 횟수 달성 여부 (기본값: false)
    rsi: '70' // RSI 기준 (기본값: 70 이상)
  });

  // 백테스팅 결과 상태
  const [backtestResults, setBacktestResults] = useState(null);
  const [dailyData, setDailyData] = useState([]);
  const [fullChartData, setFullChartData] = useState(null); // 전체 데이터 (MA 계산용)
  const [filteredChartData, setFilteredChartData] = useState(null); // 필터링된 데이터 (화면 표시용)
  const [loading, setLoading] = useState(false);

  // 이동평균선 설정 상태
  const [maSettings, setMaSettings] = useState({
    ma5: { enabled: true, color: '#ff9800', period: 5 },
    ma10: { enabled: true, color: '#00bcd4', period: 10 },
    ma20: { enabled: true, color: '#9c27b0', period: 20 },
    ma60: { enabled: false, color: '#4caf50', period: 60 },
    ma120: { enabled: false, color: '#f44336', period: 120 }
  });

  // 종목 검색 결과
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);

  // 설정 팝업 상태
  const [showSettingsPopup, setShowSettingsPopup] = useState(false);

  // 쿠키 관리 함수들
  const saveFormToCookie = () => {
    const formData = {
      stockCode,
      stockName,
      startDate,
      endDate,
      buyConditions,
      sellConditions
    };

    const cookieValue = JSON.stringify(formData);
    const maxAge = 30 * 24 * 60 * 60; // 30일
    document.cookie = `backtestingForm=${cookieValue}; max-age=${maxAge}; path=/`;
    console.log(`🍪 [백테스팅] 폼 데이터 쿠키 저장 완료`);
  };

  const loadFormFromCookie = () => {
    try {
      const cookies = document.cookie.split(';');
      const formCookie = cookies.find(cookie => cookie.trim().startsWith('backtestingForm='));

      if (formCookie) {
        const cookieValue = formCookie.split('=')[1];
        const formData = JSON.parse(decodeURIComponent(cookieValue));

        console.log(`🍪 [백테스팅] 쿠키에서 폼 데이터 복원:`, formData);

        // 상태 업데이트
        if (formData.stockCode) setStockCode(formData.stockCode);
        if (formData.stockName) setStockName(formData.stockName);
        if (formData.startDate) setStartDate(formData.startDate);
        if (formData.endDate) setEndDate(formData.endDate);
        if (formData.buyConditions) setBuyConditions(formData.buyConditions);
        if (formData.sellConditions) setSellConditions(formData.sellConditions);

        return true;
      }
    } catch (error) {
      console.error(`❌ [백테스팅] 쿠키 로드 실패:`, error);
    }

    console.log(`🍪 [백테스팅] 저장된 폼 데이터 없음`);
    return false;
  };

  // 컴포넌트 마운트 시 쿠키에서 데이터 로드 및 종료일자 기본값 설정
  useEffect(() => {
    const loaded = loadFormFromCookie();

    // 종료일자 기본값: 오늘
    if (!loaded || !endDate) {
      const today = new Date().toISOString().split('T')[0];
      setEndDate(today);
    }
  }, []);

  // 날짜에서 1일 빼는 함수
  const subtractOneDay = (dateStr) => {
    const date = new Date(dateStr.replace(/(\d{4})(\d{2})(\d{2})/, '$1-$2-$3'));
    date.setDate(date.getDate() - 1);
    return date.toISOString().split('T')[0].replace(/-/g, '');
  };

  // 종목 검색 함수
  const searchStock = async (query) => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      setShowSearchResults(false);
      return;
    }

    try {
      // 임시로 더미 데이터 사용 (실제로는 종목 검색 API 호출)
      const dummyResults = [
        { code: '005930', name: '삼성전자', market: 'KOSPI' },
        { code: '000660', name: 'SK하이닉스', market: 'KOSPI' },
        { code: '035420', name: 'NAVER', market: 'KOSPI' },
        { code: '051910', name: 'LG화학', market: 'KOSPI' },
        { code: '006400', name: '삼성SDI', market: 'KOSPI' }
      ].filter(stock =>
        stock.code.includes(query) ||
        stock.name.toLowerCase().includes(query.toLowerCase())
      );

      setSearchResults(dummyResults);
      setShowSearchResults(true);
    } catch (error) {
      console.error('종목 검색 실패:', error);
    }
  };

  // 종목 선택 핸들러
  const handleStockSelect = (stock) => {
    setStockCode(stock.code);
    setStockName(stock.name);
    setShowSearchResults(false);
    // 종목 선택 시 쿠키 저장
    setTimeout(saveFormToCookie, 100);
  };

  // input 변경 시 쿠키 저장
  const handleInputChange = (setter, value) => {
    setter(value);
    setTimeout(saveFormToCookie, 100); // 상태 업데이트 후 저장
  };

  // 전체 캔들 데이터 가져오기 함수 (기간 기반 조회)
  const fetchAllChartData = async (stockCode, sdate, edate) => {
    let allData = [];
    const sdateStr = sdate.replace(/-/g, '');
    const edateStr = edate.replace(/-/g, '');

    console.log(`📊 [백테스팅] ${stockCode} 전체 차트 데이터 수집 시작: ${sdate} ~ ${edate}`);

    try {
      // 새로운 API 형식: /api/v1/stocks/chart (POST 요청, Query Parameter 방식)
      const chartApiUrl = getApiUrl('chart-api-server', `/stocks/chart`);
      const queryParams = new URLSearchParams({
        shcode: stockCode,
        gubun: '2',
        sdate: sdateStr,
        edate: edateStr
      });

      const response = await fetch(`${chartApiUrl}?${queryParams.toString()}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`차트 API 응답 오류: ${response.status}`);
      }

      const result = await response.json();

      // 응답에서 t8410OutBlock1 배열 추출
      let chartData = null;
      if (result && result.t8410OutBlock1) {
        chartData = result.t8410OutBlock1;
      } else if (result && result.data && result.data.t8410OutBlock1) {
        chartData = result.data.t8410OutBlock1;
      }

      if (!chartData || !Array.isArray(chartData) || chartData.length === 0) {
        console.log(`📊 [백테스팅] 조회된 데이터가 없습니다.`);
        return [];
      }

      console.log(`📈 [백테스팅] ${chartData.length}개 데이터 조회 완료 (${sdateStr} ~ ${edateStr})`);
      allData = chartData;

    } catch (error) {
      console.error(`❌ [백테스팅] 차트 데이터 조회 실패:`, error);
      return [];
    }

    // 날짜 순으로 정렬 (오래된 날짜부터)
    allData.sort((a, b) => parseInt(a.date) - parseInt(b.date));

    console.log(`✅ [백테스팅] 전체 ${allData.length}개 데이터 수집 완료`);
    return allData;
  };

  // 이동평균선 계산 함수
  const calculateMA = (data, period) => {
    return data.map((item, index) => {
      if (index < period - 1) return null;
      const sum = data.slice(index - period + 1, index + 1).reduce((acc, item) => acc + (parseFloat(item.close) || 0), 0);
      return Math.round(sum / period);
    });
  };

  // RSI 계산 함수
  const calculateRSI = (data, period = 14) => {
    const rsi = [];

    for (let i = 0; i < data.length; i++) {
      if (i < period) {
        rsi.push(null);
        continue;
      }

      let gains = 0;
      let losses = 0;

      for (let j = i - period + 1; j <= i; j++) {
        const currentClose = parseFloat(data[j].close) || 0;
        const prevClose = parseFloat(data[j - 1].close) || 0;
        const change = currentClose - prevClose;
        if (change > 0) {
          gains += change;
        } else {
          losses += Math.abs(change);
        }
      }

      const avgGain = gains / period;
      const avgLoss = losses / period;

      if (avgLoss === 0) {
        rsi.push(100);
      } else {
        const rs = avgGain / avgLoss;
        rsi.push(100 - (100 / (1 + rs)));
      }
    }

    return rsi;
  };

  // MACD 계산 함수
  const calculateMACD = (data, fastPeriod = 12, slowPeriod = 26, signalPeriod = 9) => {
    // EMA 계산 헬퍼 함수
    const calculateEMA = (data, period) => {
      const ema = [];
      const k = 2 / (period + 1);

      for (let i = 0; i < data.length; i++) {
        const close = parseFloat(data[i].close) || 0;
        if (i === 0) {
          ema.push(close);
        } else {
          ema.push(close * k + ema[i - 1] * (1 - k));
        }
      }

      return ema;
    };

    const fastEMA = calculateEMA(data, fastPeriod);
    const slowEMA = calculateEMA(data, slowPeriod);

    // MACD Line
    const macdLine = fastEMA.map((fast, i) => fast - slowEMA[i]);

    // Signal Line (MACD의 EMA)
    const signalLine = [];
    const k = 2 / (signalPeriod + 1);

    for (let i = 0; i < macdLine.length; i++) {
      if (i === 0) {
        signalLine.push(macdLine[i]);
      } else {
        signalLine.push(macdLine[i] * k + signalLine[i - 1] * (1 - k));
      }
    }

    // Histogram
    const histogram = macdLine.map((macd, i) => macd - signalLine[i]);

    return {
      macdLine,
      signalLine,
      histogram
    };
  };

  // 매매 사이클 차트 렌더링 함수
  const renderCycleChart = (cycle, cycleIndex, chartData) => {
    // 10일 버퍼를 포함한 데이터 범위 계산
    const bufferDays = 10;
    const startIndex = Math.max(0, cycle.startIndex - bufferDays);
    const endIndex = Math.min(chartData.length - 1, cycle.endIndex + bufferDays);

    const cycleChartData = chartData.slice(startIndex, endIndex + 1);

    // 캔들스틱 데이터
    const candleData = cycleChartData.map((item, index) => ({
      x: index,
      y: [
        parseFloat(item.open) || 0,
        parseFloat(item.high) || 0,
        parseFloat(item.low) || 0,
        parseFloat(item.close) || 0
      ],
      originalDate: item.date
    }));

    // RSI 데이터 계산
    const rsiValues = calculateRSI(cycleChartData);
    const rsiData = rsiValues.map((value, index) => ({
      x: index,
      y: value
    })).filter(item => item.y !== null);

    // 매수/매도 마커 위치 계산
    const buyMarkers = cycle.buyDates.map(buyDate => {
      const index = cycleChartData.findIndex(item => item.date === buyDate);
      return index >= 0 ? {
        x: index,
        y: cycleChartData[index].close,
        marker: {
          size: 8,
          fillColor: '#FF4444',
          strokeColor: '#fff',
          strokeWidth: 2
        },
        label: {
          text: '매수',
          style: {
            background: '#FF4444',
            color: '#fff',
            fontSize: '10px'
          }
        }
      } : null;
    }).filter(m => m !== null);

    const sellMarkerIndex = cycleChartData.findIndex(item => item.date === cycle.sellDate);
    const sellMarker = sellMarkerIndex >= 0 ? {
      x: sellMarkerIndex,
      y: cycleChartData[sellMarkerIndex].close,
      marker: {
        size: 8,
        fillColor: '#4444FF',
        strokeColor: '#fff',
        strokeWidth: 2
      },
      label: {
        text: '매도',
        style: {
          background: '#4444FF',
          color: '#fff',
          fontSize: '10px'
        }
      }
    } : null;

    // 캔들 차트 옵션
    const candleOptions = {
      chart: {
        type: 'candlestick',
        height: 300,
        toolbar: { show: false },
        zoom: { enabled: false }
      },
      title: {
        text: `사이클 #${cycleIndex + 1} - 캔들 차트 (수익률: ${cycle.profitRate.toFixed(2)}%)`,
        align: 'left',
        style: {
          fontSize: '14px',
          fontWeight: 'bold',
          color: cycle.profitRate >= 0 ? '#d32f2f' : '#1976d2'
        }
      },
      xaxis: {
        type: 'category',
        categories: cycleChartData.map((item, index) => {
          if (item.date.length === 8) {
            const month = item.date.substring(4, 6);
            const day = item.date.substring(6, 8);
            return `${parseInt(month)}/${parseInt(day)}`;
          }
          return index.toString();
        }),
        labels: {
          rotate: -45,
          style: { fontSize: '9px' }
        }
      },
      yaxis: {
        tooltip: { enabled: true },
        labels: {
          formatter: (val) => val ? val.toLocaleString() : ''
        }
      },
      plotOptions: {
        candlestick: {
          colors: {
            upward: '#d32f2f',
            downward: '#1976d2'
          }
        }
      },
      annotations: {
        points: [...buyMarkers, sellMarker].filter(m => m !== null)
      },
      grid: {
        borderColor: '#e0e0e0'
      }
    };

    const candleSeries = [{
      name: 'Price',
      data: candleData
    }];

    // RSI 차트 옵션
    const rsiOptions = {
      chart: {
        type: 'line',
        height: 150,
        toolbar: { show: false },
        zoom: { enabled: false }
      },
      title: {
        text: 'RSI 지표',
        align: 'left',
        style: {
          fontSize: '12px',
          color: '#666'
        }
      },
      xaxis: {
        type: 'category',
        categories: cycleChartData.map((item, index) => index),
        labels: { show: false }
      },
      yaxis: {
        min: 0,
        max: 100,
        tickAmount: 4,
        labels: {
          formatter: (val) => val ? val.toFixed(0) : ''
        }
      },
      stroke: {
        width: 2,
        curve: 'smooth'
      },
      colors: ['#9c27b0'],
      grid: {
        borderColor: '#e0e0e0'
      },
      annotations: {
        yaxis: [
          {
            y: 70,
            borderColor: '#FF4444',
            label: {
              text: '과매수(70)',
              style: {
                color: '#fff',
                background: '#FF4444',
                fontSize: '9px'
              }
            }
          },
          {
            y: 30,
            borderColor: '#4444FF',
            label: {
              text: '과매도(30)',
              style: {
                color: '#fff',
                background: '#4444FF',
                fontSize: '9px'
              }
            }
          }
        ]
      }
    };

    const rsiSeries = [{
      name: 'RSI',
      data: rsiData
    }];

    return (
      <div key={cycleIndex} className="cycle-chart-container">
        <div className="cycle-header">
          <h4>📊 매매 사이클 #{cycleIndex + 1}</h4>
          <div className="cycle-info">
            <span className="info-item">
              매수: {cycle.buyDates.map(d => d.replace(/(\d{4})(\d{2})(\d{2})/, '$2/$3')).join(', ')}
            </span>
            <span className="info-item">
              매도: {cycle.sellDate.replace(/(\d{4})(\d{2})(\d{2})/, '$2/$3')}
            </span>
            <span className="info-item">
              평균매수가: {cycle.avgBuyPrice.toFixed(0).toLocaleString()}원
            </span>
            <span className="info-item">
              매도가: {cycle.sellPrice.toLocaleString()}원
            </span>
            <span className={`info-item profit ${cycle.profitRate >= 0 ? 'positive' : 'negative'}`}>
              수익률: {cycle.profitRate >= 0 ? '+' : ''}{cycle.profitRate.toFixed(2)}%
            </span>
            {cycle.isForceClose && (
              <span className="info-item force-close">강제청산</span>
            )}
          </div>
        </div>
        <div className="cycle-charts">
          <ReactApexChart
            options={candleOptions}
            series={candleSeries}
            type="candlestick"
            height={300}
          />
          <ReactApexChart
            options={rsiOptions}
            series={rsiSeries}
            type="line"
            height={150}
          />
        </div>
      </div>
    );
  };

  // ApexChart 렌더링 함수 (거래량 포함, 이동평균선 설정 반영)
  const renderApexChart = (filteredData, fullData) => {
    console.log(`🔄 [백테스팅] 차트 렌더링 시작:`, {
      필터링된데이터: filteredData?.length || 0,
      전체데이터: fullData?.length || 0
    });

    // 로딩 중이거나 데이터가 없는 경우
    if (!filteredData || !Array.isArray(filteredData) || filteredData.length === 0) {
      return (
        <div style={{
          width: '100%',
          height: '600px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#666',
          backgroundColor: '#f8f9fa',
          border: '1px solid #e0e0e0',
          borderRadius: '8px'
        }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ marginBottom: '8px' }}>📊</div>
            <div>일봉 차트 로딩 중...</div>
          </div>
        </div>
      );
    }

    // 표시할 데이터는 필터링된 데이터 사용 (최대 100개)
    const displayCandleCount = Math.min(100, filteredData.length);
    const displayData = filteredData.slice(-displayCandleCount);

    // ApexCharts 캔들스틱 데이터 형식으로 변환 (연속적 인덱스 사용)
    const chartData = displayData.map((item, index) => ({
      x: index,
      y: [
        parseFloat(item.open) || 0,
        parseFloat(item.high) || 0,
        parseFloat(item.low) || 0,
        parseFloat(item.close) || 0
      ],
      originalDate: item.date
    }));

    // 거래량 데이터
    const volumeData = displayData.map((item, index) => ({
      x: index,
      y: parseFloat(item.volume) || parseFloat(item.jdiff_vol) || 0
    }));

    const options = {
      chart: {
        type: 'candlestick',
        height: 600,
        background: 'transparent',
        toolbar: {
          show: false
        },
        zoom: {
          enabled: false
        },
        animations: {
          enabled: false
        }
      },
      title: {
        text: `${stockName || stockCode} 일봉 차트 (${displayData.length}개)`,
        align: 'center',
        style: {
          fontSize: '16px',
          fontWeight: 'bold',
          color: '#333'
        }
      },
      xaxis: {
        type: 'category',
        categories: displayData.map((item, index) => {
          if (item && item.date && typeof item.date === 'string') {
            // YYYYMMDD 형식 처리
            if (item.date.length === 8) {
              const month = item.date.substring(4, 6);
              const day = item.date.substring(6, 8);
              return `${parseInt(month)}/${parseInt(day)}`;
            }
            // YYYY-MM-DD 형식 처리
            else if (item.date.includes('-')) {
              const [year, month, day] = item.date.split('-');
              return `${parseInt(month)}/${parseInt(day)}`;
            }
          }
          return index.toString();
        }),
        tickAmount: Math.min(displayData.length, 10),
        labels: {
          style: {
            colors: '#666',
            fontSize: '10px'
          },
          rotate: -45,
          rotateAlways: true,
          hideOverlappingLabels: false,
          trim: false,
          show: true
        }
      },
      yaxis: [
        {
          seriesName: 'Price',
          tooltip: {
            enabled: true
          },
          labels: {
            formatter: function (val) {
              return val ? val.toLocaleString() : '';
            }
          }
        },
        {
          seriesName: 'Volume',
          opposite: true,
          show: false,
          labels: {
            formatter: function (val) {
              return val ? val.toLocaleString() : '';
            }
          }
        }
      ],
      plotOptions: {
        candlestick: {
          colors: {
            upward: '#d32f2f', // 한국식 빨강 = 상승
            downward: '#1976d2' // 한국식 파랑 = 하락
          },
          wick: {
            useFillColor: true
          }
        },
        bar: {
          columnWidth: '80%',
          colors: {
            ranges: [{
              from: 0,
              to: Infinity,
              color: '#90CAF9'
            }]
          }
        }
      },
      grid: {
        show: true,
        borderColor: '#e0e0e0',
        strokeDashArray: 3,
        xaxis: {
          lines: {
            show: true
          }
        },
        yaxis: {
          lines: {
            show: true
          }
        }
      },
      tooltip: {
        enabled: true,
        theme: 'light',
        custom: function({ seriesIndex, dataPointIndex, w }) {
          const data = displayData[dataPointIndex];
          if (!data) return '';

          let formattedDate = 'N/A';
          if (data.date && typeof data.date === 'string') {
            // YYYYMMDD 형식 처리
            if (data.date.length === 8) {
              formattedDate = data.date.replace(/(\d{4})(\d{2})(\d{2})/, '$1-$2-$3');
            }
            // 이미 YYYY-MM-DD 형식인 경우
            else if (data.date.includes('-')) {
              formattedDate = data.date;
            }
          }

          return `
            <div class="custom-tooltip">
              <div><strong>날짜:</strong> ${formattedDate}</div>
              <div><strong>시가:</strong> ${data.open?.toLocaleString() || 'N/A'}</div>
              <div><strong>고가:</strong> ${data.high?.toLocaleString() || 'N/A'}</div>
              <div><strong>저가:</strong> ${data.low?.toLocaleString() || 'N/A'}</div>
              <div><strong>종가:</strong> ${data.close?.toLocaleString() || 'N/A'}</div>
              <div><strong>거래량:</strong> ${data.volume?.toLocaleString() || 'N/A'}</div>
            </div>
          `;
        }
      },
      stroke: {
        width: 1
      },
      responsive: [
        {
          breakpoint: 768,
          options: {
            chart: {
              height: 400
            },
            title: {
              style: {
                fontSize: '14px'
              }
            }
          }
        }
      ]
    };

    // 이동평균선 데이터 계산 및 시리즈 생성
    const series = [
      {
        name: 'Price',
        type: 'candlestick',
        data: chartData
      },
      {
        name: 'Volume',
        type: 'bar',
        data: volumeData,
        yAxisIndex: 1
      }
    ];

    // 이동평균선 계산에는 전체 데이터 사용 (maSettings 반영)
    if (fullData && fullData.length > 0) {
      Object.entries(maSettings).forEach(([key, config]) => {
        if (config.enabled && fullData.length >= config.period) {
          // 전체 데이터로 이동평균 계산
          const fullMAData = calculateMA(fullData, config.period);

          // 필터링된 범위에 해당하는 MA 데이터만 추출
          const startDateStr = filteredData[0]?.date;
          const endDateStr = filteredData[filteredData.length - 1]?.date;
          const startIndex = fullData.findIndex(item => item.date === startDateStr);
          const endIndex = fullData.findIndex(item => item.date === endDateStr);

          if (startIndex >= 0 && endIndex >= 0) {
            // 필터링된 데이터와 동일한 범위의 MA만 추출
            const relevantMAData = fullMAData.slice(startIndex, endIndex + 1);

            // displayData 길이와 동일하게 맞추기 (최대 100개)
            const slicedMAData = relevantMAData.slice(-displayCandleCount);

            const maChartData = slicedMAData.map((value, index) => ({
              x: index,
              y: value
            })).filter(item => item.y !== null);

            if (maChartData.length > 0) {
              series.push({
                name: `MA${config.period}`,
                type: 'line',
                data: maChartData,
                color: config.color,
                yAxisIndex: 0
              });
            }
          }
        }
      });
    }

    return (
      <div style={{ width: '100%', height: '600px' }}>
        <ReactApexChart
          options={options}
          series={series}
          type="candlestick"
          height={600}
        />
      </div>
    );
  };

  // 백테스팅 알고리즘 실행
  const executeBacktestingAlgorithm = (chartData) => {
    console.log(`🤖 [백테스팅] 알고리즘 실행 시작`);

    // 매수/매도 조건 파싱
    const maPeriod = parseInt(buyConditions.maType);
    const maDeviation = parseFloat(buyConditions.maDeviation) / 100; // -10 → -0.1
    const buyRsiThreshold = parseFloat(buyConditions.rsi);
    const maxBuyCount = parseInt(buyConditions.maxBuyCount);
    const sellRsiThreshold = parseFloat(sellConditions.rsi);
    const requireMaxBuyCount = sellConditions.requireMaxBuyCount;

    // 이동평균선 및 RSI 계산
    const maData = calculateMA(chartData, maPeriod);
    const rsiData = calculateRSI(chartData);

    // 거래 사이클 저장
    const tradingCycles = [];
    let currentCycle = null;

    // 날짜별 반복
    for (let i = 0; i < chartData.length; i++) {
      const day = chartData[i];
      const ma = maData[i];
      const rsi = rsiData[i];
      const price = day.close;

      // MA와 RSI가 계산되지 않은 초기 구간은 스킵
      if (ma === null || rsi === null) continue;

      // 매수 신호 확인
      if (!currentCycle || (currentCycle && currentCycle.buyCount < maxBuyCount)) {
        // 매수 조건: 현재가 <= MA * (1 + 이격률) AND RSI <= 기준값
        const maBuyThreshold = ma * (1 + maDeviation);

        if (price <= maBuyThreshold && rsi <= buyRsiThreshold) {
          if (!currentCycle) {
            // 새로운 사이클 시작
            currentCycle = {
              buyDates: [day.date],
              buyPrices: [price],
              buyCount: 1,
              sellDate: null,
              sellPrice: null,
              startIndex: i
            };
            console.log(`💰 [매수 #1] ${day.date}: ${price.toLocaleString()}원 (MA: ${ma.toFixed(0)}, RSI: ${rsi.toFixed(1)})`);
          } else {
            // 분할 매수
            currentCycle.buyDates.push(day.date);
            currentCycle.buyPrices.push(price);
            currentCycle.buyCount++;
            console.log(`💰 [매수 #${currentCycle.buyCount}] ${day.date}: ${price.toLocaleString()}원 (MA: ${ma.toFixed(0)}, RSI: ${rsi.toFixed(1)})`);
          }
        }
      }

      // 매도 신호 확인 (매수 포지션이 있을 때만)
      if (currentCycle && !currentCycle.sellDate) {
        let shouldSell = false;

        // 매도 조건 1: RSI >= 기준값
        if (rsi >= sellRsiThreshold) {
          // 매도 조건 2: requireMaxBuyCount가 true인 경우, 최대 매수 횟수 달성 확인
          if (requireMaxBuyCount) {
            if (currentCycle.buyCount >= maxBuyCount) {
              shouldSell = true;
            }
          } else {
            shouldSell = true;
          }
        }

        if (shouldSell) {
          // 매도 실행
          currentCycle.sellDate = day.date;
          currentCycle.sellPrice = price;
          currentCycle.endIndex = i;

          // 평균 매수가 계산
          const avgBuyPrice = currentCycle.buyPrices.reduce((a, b) => a + b, 0) / currentCycle.buyCount;
          const profitRate = ((price - avgBuyPrice) / avgBuyPrice) * 100;

          currentCycle.avgBuyPrice = avgBuyPrice;
          currentCycle.profitRate = profitRate;

          console.log(`💸 [매도] ${day.date}: ${price.toLocaleString()}원 (평균 매수가: ${avgBuyPrice.toFixed(0)}, 수익률: ${profitRate.toFixed(2)}%, RSI: ${rsi.toFixed(1)})`);

          // 사이클 저장
          tradingCycles.push({ ...currentCycle });
          currentCycle = null;
        }
      }
    }

    // 미체결 포지션 처리 (백테스팅 종료 시점에 남아있는 경우)
    if (currentCycle && !currentCycle.sellDate) {
      console.warn(`⚠️ [미체결] 백테스팅 종료 시점에 미체결 포지션이 있습니다. (매수 ${currentCycle.buyCount}회)`);
      // 마지막 날 종가로 강제 매도 처리 (옵션)
      const lastDay = chartData[chartData.length - 1];
      currentCycle.sellDate = lastDay.date;
      currentCycle.sellPrice = lastDay.close;
      currentCycle.endIndex = chartData.length - 1;

      const avgBuyPrice = currentCycle.buyPrices.reduce((a, b) => a + b, 0) / currentCycle.buyCount;
      const profitRate = ((lastDay.close - avgBuyPrice) / avgBuyPrice) * 100;

      currentCycle.avgBuyPrice = avgBuyPrice;
      currentCycle.profitRate = profitRate;
      currentCycle.isForceClose = true;

      tradingCycles.push({ ...currentCycle });
    }

    console.log(`✅ [백테스팅] 알고리즘 완료: 총 ${tradingCycles.length}개 사이클`);
    return tradingCycles;
  };

  // 백테스팅 실행 함수
  const runBacktest = async () => {
    if (!stockCode || !startDate || !endDate) {
      alert('종목 코드, 시작 날짜, 종료 날짜를 모두 입력해주세요.');
      return;
    }

    setLoading(true);
    try {
      // 1. 전체 차트 데이터 가져오기
      console.log(`🚀 [백테스팅] 실행 시작: ${stockCode} (${startDate} ~ ${endDate})`);
      const rawChartData = await fetchAllChartData(stockCode, startDate, endDate);

      if (!rawChartData || rawChartData.length === 0) {
        alert('차트 데이터를 가져올 수 없습니다. 종목 코드를 확인해주세요.');
        setLoading(false);
        return;
      }

      // 2. 차트 데이터 설정 및 필터링
      // 전체 데이터 저장 (이동평균선 계산용)
      setFullChartData(rawChartData);

      // 시작날짜 이후 데이터만 필터링 (화면 표시용)
      const startDateStr = startDate.replace(/-/g, '');
      const filteredData = rawChartData.filter(item => {
        return item.date >= startDateStr;
      });

      setFilteredChartData(filteredData);
      setDailyData(filteredData);

      console.log(`📊 [백테스팅] 차트 데이터 설정 완료:`, {
        전체데이터: rawChartData.length,
        표시데이터: filteredData.length,
        시작날짜: startDateStr,
        시작일: filteredData[0]?.date,
        종료일: filteredData[filteredData.length - 1]?.date
      });

      // 3. 백테스팅 알고리즘 실행
      const tradingCycles = executeBacktestingAlgorithm(rawChartData);

      // 4. 결과 계산
      const totalCycles = tradingCycles.length;
      const winCycles = tradingCycles.filter(c => c.profitRate > 0).length;
      const lossCycles = tradingCycles.filter(c => c.profitRate <= 0).length;
      const winRate = totalCycles > 0 ? (winCycles / totalCycles * 100).toFixed(2) : 0;

      const totalProfitRate = tradingCycles.reduce((sum, c) => sum + c.profitRate, 0);
      const avgProfitRate = totalCycles > 0 ? (totalProfitRate / totalCycles).toFixed(2) : 0;

      const results = {
        totalCycles,
        winCycles,
        lossCycles,
        winRate: parseFloat(winRate),
        avgProfitRate: parseFloat(avgProfitRate),
        totalProfitRate: totalProfitRate.toFixed(2),
        tradingCycles
      };

      setBacktestResults(results);
      console.log(`✅ [백테스팅] 완료:`, results);

    } catch (error) {
      console.error('❌ [백테스팅] 실행 실패:', error);
      alert('백테스팅 실행 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // 임시 일봉 데이터 생성 함수
  const generateMockDailyData = (start, end) => {
    const data = [];
    const startDateObj = new Date(start);
    const endDateObj = new Date(end);
    let currentDate = new Date(startDateObj);
    let currentPrice = 75000; // 시작 가격

    while (currentDate <= endDateObj) {
      // 주말 제외
      if (currentDate.getDay() !== 0 && currentDate.getDay() !== 6) {
        const change = (Math.random() - 0.5) * 0.05; // -2.5% ~ +2.5%
        const open = currentPrice;
        const close = currentPrice * (1 + change);
        const high = Math.max(open, close) * (1 + Math.random() * 0.02);
        const low = Math.min(open, close) * (1 - Math.random() * 0.02);
        const volume = Math.floor(Math.random() * 1000000) + 100000;

        data.push({
          date: currentDate.toISOString().split('T')[0],
          open: Math.round(open),
          high: Math.round(high),
          low: Math.round(low),
          close: Math.round(close),
          volume: volume,
          change: ((close - open) / open * 100).toFixed(2)
        });

        currentPrice = close;
      }
      currentDate.setDate(currentDate.getDate() + 1);
    }

    return data.reverse(); // 최신 날짜부터 표시
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>📊 알고리즘 백테스팅</h1>
        <p>매매 조건에 따른 투자 전략의 과거 성과를 분석하세요</p>
      </div>

      <div className="backtesting-container">
        {/* 설정 패널 - 3개 영역을 한 줄로 배치 (PC) */}
        <div className="backtesting-settings-grid">
            {/* 종목 설정 영역 */}
            <div className="settings-section">
            <h3>🎯 종목 설정</h3>
            <div className="stock-search-container">
              <div className="form-group">
                <label>종목 검색</label>
                <input
                  type="text"
                  placeholder="종목 코드 또는 종목명 입력"
                  value={stockCode || stockName}
                  onChange={(e) => {
                    const value = e.target.value;
                    handleInputChange(setStockCode, value);
                    setStockName('');
                    searchStock(value);
                  }}
                  className="stock-search-input"
                />
                {showSearchResults && searchResults.length > 0 && (
                  <div className="search-results">
                    {searchResults.map((stock, index) => (
                      <div
                        key={index}
                        className="search-result-item"
                        onClick={() => handleStockSelect(stock)}
                      >
                        <span className="stock-code">{stock.code}</span>
                        <span className="stock-name">{stock.name}</span>
                        <span className="stock-market">{stock.market}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {stockCode && stockName && (
                <div className="selected-stock">
                  선택된 종목: <strong>{stockName} ({stockCode})</strong>
                </div>
              )}
            </div>

            <div className="form-group" style={{ marginTop: '1rem' }}>
              <label>시작 날짜</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => handleInputChange(setStartDate, e.target.value)}
              />
            </div>
            <div className="form-group">
              <label>종료 날짜</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => handleInputChange(setEndDate, e.target.value)}
              />
            </div>
          </div>

          {/* 매수 조건 영역 */}
          <div className="settings-section">
            <h3>💰 매수 조건</h3>
            <div className="condition-inputs">
              <div className="form-group">
                <label>이동평균선 종류</label>
                <select
                  value={buyConditions.maType}
                  onChange={(e) => {
                    const newConditions = {...buyConditions, maType: e.target.value};
                    handleInputChange(setBuyConditions, newConditions);
                  }}
                >
                  <option value="5">5일</option>
                  <option value="10">10일</option>
                  <option value="20">20일</option>
                  <option value="60">60일</option>
                  <option value="120">120일</option>
                </select>
              </div>
              <div className="form-group">
                <label>이동평균선 이격률 (%)</label>
                <input
                  type="number"
                  placeholder="예: -10 (이평선 대비 -10%)"
                  value={buyConditions.maDeviation}
                  onChange={(e) => {
                    const newConditions = {...buyConditions, maDeviation: e.target.value};
                    handleInputChange(setBuyConditions, newConditions);
                  }}
                />
              </div>
              <div className="form-group">
                <label>RSI 기준 (이하)</label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  placeholder="예: 30"
                  value={buyConditions.rsi}
                  onChange={(e) => {
                    const newConditions = {...buyConditions, rsi: e.target.value};
                    handleInputChange(setBuyConditions, newConditions);
                  }}
                />
              </div>
              <div className="form-group">
                <label>최대 분할 매수 횟수</label>
                <input
                  type="number"
                  min="1"
                  max="10"
                  placeholder="예: 3"
                  value={buyConditions.maxBuyCount}
                  onChange={(e) => {
                    const newConditions = {...buyConditions, maxBuyCount: e.target.value};
                    handleInputChange(setBuyConditions, newConditions);
                  }}
                />
              </div>
            </div>
          </div>

          {/* 매도 조건 영역 */}
          <div className="settings-section">
            <h3>💸 매도 조건</h3>
            <div className="condition-inputs">
              <div className="form-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    checked={sellConditions.requireMaxBuyCount}
                    onChange={(e) => {
                      const newConditions = {...sellConditions, requireMaxBuyCount: e.target.checked};
                      handleInputChange(setSellConditions, newConditions);
                    }}
                  />
                  <span style={{ marginLeft: '8px' }}>최대 분할 매수 횟수 달성 필수</span>
                </label>
              </div>
              <div className="form-group">
                <label>RSI 기준 (이상)</label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  placeholder="예: 70 (RSI 70 이상 시 매도)"
                  value={sellConditions.rsi}
                  onChange={(e) => {
                    const newConditions = {...sellConditions, rsi: e.target.value};
                    handleInputChange(setSellConditions, newConditions);
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* 백테스팅 실행 버튼 */}
        <div className="run-backtest-section">
          <button
            onClick={runBacktest}
            disabled={loading || !stockCode || !startDate || !endDate}
            className="run-backtest-btn"
          >
            {loading ? '백테스팅 실행 중...' : '🚀 백테스팅 실행'}
          </button>
        </div>

        {/* 차트 영역 - 항상 표시 */}
        <div className="chart-section">
          <div className="chart-header">
            <h3>📈 주가 차트</h3>
            <button
              className="chart-settings-btn"
              onClick={() => setShowSettingsPopup(true)}
              title="차트 설정"
            >
              ⚙️
            </button>
          </div>
          <div className="chart-container">
            {backtestResults ? (
              renderApexChart(filteredChartData, fullChartData)
            ) : (
              <div className="chart-placeholder">
                <div className="placeholder-content">
                  <div className="placeholder-icon">📊</div>
                  <div className="placeholder-text">백테스팅을 실행하면 차트가 표시됩니다</div>
                  <div className="placeholder-subtext">
                    종목, 날짜, 기간을 설정하고 백테스팅 실행 버튼을 클릭하세요
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* 결과 패널 */}
        {backtestResults && (
          <div className="backtesting-results">
            <div className="results-section">
              <h3>📈 백테스팅 결과 요약</h3>
              <div className="results-summary">
                <div className="result-card">
                  <div className="result-label">총 매매 사이클</div>
                  <div className="result-value">
                    {backtestResults.totalCycles}회
                  </div>
                </div>
                <div className="result-card">
                  <div className="result-label">수익 사이클</div>
                  <div className="result-value positive">
                    {backtestResults.winCycles}회
                  </div>
                </div>
                <div className="result-card">
                  <div className="result-label">손실 사이클</div>
                  <div className="result-value negative">
                    {backtestResults.lossCycles}회
                  </div>
                </div>
                <div className="result-card">
                  <div className="result-label">승률</div>
                  <div className={`result-value ${backtestResults.winRate >= 50 ? 'positive' : 'negative'}`}>
                    {backtestResults.winRate}%
                  </div>
                </div>
                <div className="result-card">
                  <div className="result-label">평균 수익률</div>
                  <div className={`result-value ${backtestResults.avgProfitRate >= 0 ? 'positive' : 'negative'}`}>
                    {backtestResults.avgProfitRate >= 0 ? '+' : ''}{backtestResults.avgProfitRate}%
                  </div>
                </div>
                <div className="result-card">
                  <div className="result-label">총 누적 수익률</div>
                  <div className={`result-value ${backtestResults.totalProfitRate >= 0 ? 'positive' : 'negative'}`}>
                    {backtestResults.totalProfitRate >= 0 ? '+' : ''}{backtestResults.totalProfitRate}%
                  </div>
                </div>
              </div>
            </div>

            {/* 매매 사이클 차트 섹션 */}
            {backtestResults.tradingCycles && backtestResults.tradingCycles.length > 0 && (
              <div className="results-section">
                <h3>📊 매매 사이클별 차트 분석</h3>
                <div className="cycles-container">
                  {backtestResults.tradingCycles.map((cycle, index) =>
                    renderCycleChart(cycle, index, fullChartData)
                  )}
                </div>
              </div>
            )}

            <div className="results-section">
              <h3>📋 일봉 데이터</h3>
              <div className="daily-data-table">
                <table>
                  <thead>
                    <tr>
                      <th>날짜</th>
                      <th>시가</th>
                      <th>고가</th>
                      <th>저가</th>
                      <th>종가</th>
                      <th>변동률</th>
                      <th>거래량</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dailyData.slice(0, 50).map((day, index) => {
                      const change = ((day.close - day.open) / day.open * 100).toFixed(2);
                      return (
                        <tr key={index}>
                          <td>{day.date}</td>
                          <td>{parseInt(day.open).toLocaleString()}</td>
                          <td>{parseInt(day.high).toLocaleString()}</td>
                          <td>{parseInt(day.low).toLocaleString()}</td>
                          <td>{parseInt(day.close).toLocaleString()}</td>
                          <td className={`change ${parseFloat(change) >= 0 ? 'positive' : 'negative'}`}>
                            {parseFloat(change) >= 0 ? '+' : ''}{change}%
                          </td>
                          <td>{parseInt(day.volume).toLocaleString()}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {dailyData.length > 50 && (
                  <div className="table-footer">
                    최근 50일 데이터만 표시됩니다. (전체 {dailyData.length}일)
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* 설정 팝업 모달 */}
        {showSettingsPopup && (
          <div className="settings-popup-overlay" onClick={() => setShowSettingsPopup(false)}>
            <div className="settings-popup-content" onClick={(e) => e.stopPropagation()}>
              <div className="settings-popup-header">
                <h3>⚙️ 차트 설정</h3>
                <button
                  className="settings-close-btn"
                  onClick={() => setShowSettingsPopup(false)}
                >
                  ✕
                </button>
              </div>
              <div className="settings-popup-body">
                <h4>📊 이동평균선 설정</h4>
                <div className="ma-settings-list">
                  {Object.entries(maSettings).map(([key, config]) => (
                    <div key={key} className="ma-setting-row">
                      <label className="ma-checkbox-label">
                        <input
                          type="checkbox"
                          checked={config.enabled}
                          onChange={(e) => {
                            setMaSettings({
                              ...maSettings,
                              [key]: { ...config, enabled: e.target.checked }
                            });
                          }}
                        />
                        <span className="ma-label-text">MA{config.period}</span>
                      </label>
                      <div className="ma-controls">
                        <label className="ma-control-label">
                          색상
                          <input
                            type="color"
                            value={config.color}
                            onChange={(e) => {
                              setMaSettings({
                                ...maSettings,
                                [key]: { ...config, color: e.target.value }
                              });
                            }}
                            className="ma-color-picker"
                            disabled={!config.enabled}
                          />
                        </label>
                        <label className="ma-control-label">
                          기간
                          <input
                            type="number"
                            value={config.period}
                            onChange={(e) => {
                              setMaSettings({
                                ...maSettings,
                                [key]: { ...config, period: parseInt(e.target.value) || config.period }
                              });
                            }}
                            className="ma-period-input"
                            disabled={!config.enabled}
                            min="1"
                            max="250"
                          />
                        </label>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="settings-popup-footer">
                <button
                  className="settings-apply-btn"
                  onClick={() => setShowSettingsPopup(false)}
                >
                  적용
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AlgorithmBacktesting;